/**
 * „Meine Werkstattaufträge"-Tab (Jul-2026, User-Wunsch).
 *
 * Zeigt offene Werkstattaufträge. Für Mitarbeiter (zweiradverkaeufer) werden nur
 * die eigenen Aufträge angezeigt (angenommen oder zugewiesen). Klick öffnet den
 * Auftrag in der Werkstatt.
 *
 * Datenquelle: GET /api/work-orders?status=open
 */
import { useEffect, useState } from "react";
import { Loader2, Wrench, ArrowRight, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";

const vehLabel = (v = {}) => {
    const s = [v.brand, v.model].filter(Boolean).join(" ");
    return v.license_plate ? `${s} (${v.license_plate})`.trim() : (s || "Fahrzeug");
};

// Jul-2026 (User-Wunsch) — Status auf Deutsch anzeigen
const STATUS_DE = {
    draft: "Entwurf", quote: "Angebot", confirmed: "Auftrag", in_progress: "In Arbeit",
    completed: "Fertig", invoiced: "Abgerechnet", cancelled: "Storniert",
};

export default function MyWorkshopOrdersTab() {
    const { user } = useAuth();
    const [items, setItems] = useState(null);

    useEffect(() => {
        let on = true;
        api.get("/work-orders", { params: { status: "open" } })
            .then((r) => {
                if (!on) return;
                let rows = Array.isArray(r.data) ? r.data : (r.data?.items || []);
                if (user?.role === "zweiradverkaeufer") {
                    rows = rows.filter((w) =>
                        w.accepted_by_id === user.id || (user.name && w.employee === user.name));
                }
                setItems(rows);
            })
            .catch(() => { if (on) setItems([]); });
        return () => { on = false; };
    }, [user]);

    if (items === null) {
        return <div className="p-6 flex items-center gap-2 text-gray-500 text-sm" data-testid="my-workshop-loading"><Loader2 className="w-4 h-4 animate-spin" /> Lade Werkstattaufträge…</div>;
    }
    if (items.length === 0) {
        return <div className="bg-white border border-gray-200 rounded-sm p-6 text-sm text-gray-500" data-testid="my-workshop-empty">Keine offenen Werkstattaufträge.</div>;
    }

    return (
        <div className="bg-white border border-gray-200 rounded-sm divide-y divide-gray-100" data-testid="my-workshop-tab">
            {items.map((w) => (
                <div key={w.id} className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 transition-colors">
                    <button
                        type="button"
                        onClick={() => window.open(`/dealer/workshop-orders?focus=${w.id}`, "_blank", "noopener")}
                        className="flex items-center gap-3 text-left min-w-0 flex-1"
                        data-testid={`my-workshop-row-${w.id}`}
                    >
                        <Wrench className="w-4 h-4 text-[#FFCC00] shrink-0" />
                        <div className="min-w-0 flex-1">
                            <div className="font-bold text-sm text-[#111827] truncate">
                                {w.order_number || "—"} · {vehLabel(w.vehicle)}
                            </div>
                            <div className="text-xs text-gray-500">{STATUS_DE[w.status] || w.status}</div>
                        </div>
                        <ArrowRight className="w-4 h-4 text-gray-400 shrink-0" />
                    </button>
                    {w.status !== "invoiced" && (
                        <button
                            type="button"
                            onClick={async () => {
                                if (!window.confirm(`Auftrag ${w.order_number || ""} wirklich endgültig löschen?`)) return;
                                try {
                                    await api.delete(`/work-orders/${w.id}`);
                                    toast.success("Auftrag gelöscht");
                                    setItems((rows) => rows.filter((r) => r.id !== w.id));
                                } catch (e) {
                                    toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen");
                                }
                            }}
                            className="shrink-0 text-gray-400 hover:text-red-600"
                            title="Auftrag löschen"
                            data-testid={`my-workshop-delete-${w.id}`}
                        >
                            <Trash2 className="w-4 h-4" />
                        </button>
                    )}
                </div>
            ))}
        </div>
    );
}
